// History Service for managing browser storage of visited projects and UUIDs

const HISTORY_KEY = 'timestorage_history'
const MAX_PROJECTS = 10
const MAX_UUIDS = 30

/**
 * Snapshot of how a QR tag should be presented, taken when it was last opened.
 * The history lives in localStorage and must render without hitting the API, so
 * the already formatted strings travel with the entry.
 */
export interface HistoryWindowRoom {
  positionNumber?: string
  floor?: string
  description?: string
  frameModel?: string
  frameFinish?: string
}

export interface HistoryItem {
  uuid: string
  timestamp: number
  type: 'project' | 'uuid'
  identification?: string
  subIdentification?: string
  windowRoom?: HistoryWindowRoom
}

export interface HistoryData {
  projects: HistoryItem[]
  uuids: HistoryItem[]
}

class HistoryService {
  private getHistory(): HistoryData {
    try {
      const stored = localStorage.getItem(HISTORY_KEY)
      if (stored) {
        return JSON.parse(stored)
      }
    } catch (error) {
      console.error('Error reading history from localStorage:', error)
    }
    return { projects: [], uuids: [] }
  }

  private saveHistory(data: HistoryData): void {
    try {
      localStorage.setItem(HISTORY_KEY, JSON.stringify(data))
    } catch (error) {
      console.error('Error saving history to localStorage:', error)
    }
  }

  addProject(uuid: string, identification?: string, subIdentification?: string): void {
    const history = this.getHistory()
    
    if (identification) {
      history.projects = history.projects.filter(item => item.identification !== identification)
    } else {
      history.projects = history.projects.filter(item => item.uuid !== uuid)
    }
    
    // Add new entry at the beginning
    history.projects.unshift({
      uuid,
      timestamp: Date.now(),
      type: 'project',
      identification,
      subIdentification
    })
    
    // Keep only the most recent MAX_PROJECTS
    history.projects = history.projects.slice(0, MAX_PROJECTS)
    
    this.saveHistory(history)
  }

  addUUID(
    uuid: string,
    identification?: string,
    subIdentification?: string,
    windowRoom?: HistoryWindowRoom
  ): void {
    const history = this.getHistory()

    // Remove existing entry if present
    history.uuids = history.uuids.filter(item => item.uuid !== uuid)

    // Add new entry at the beginning
    history.uuids.unshift({
      uuid,
      timestamp: Date.now(),
      type: 'uuid',
      identification,
      subIdentification,
      windowRoom
    })
    
    // Keep only the most recent MAX_UUIDS
    history.uuids = history.uuids.slice(0, MAX_UUIDS)
    
    this.saveHistory(history)
  }

  getProjects(): HistoryItem[] {
    const history = this.getHistory()
    // Deduplicate projects by identification, keeping the most recent entry for each
    const uniqueProjects: Map<string, HistoryItem> = new Map()
    
    history.projects.forEach(project => {
      const key = project.identification || project.uuid
      const existing = uniqueProjects.get(key)
      
      // Keep the most recent entry (higher timestamp)
      if (!existing || project.timestamp > existing.timestamp) {
        uniqueProjects.set(key, project)
      }
    })
    
    // Convert back to array and sort by timestamp (most recent first)
    return Array.from(uniqueProjects.values()).sort((a, b) => b.timestamp - a.timestamp)
  }

  getUUIDs(): HistoryItem[] {
    const history = this.getHistory()
    return history.uuids
  }

  clearHistory(): void {
    this.saveHistory({ projects: [], uuids: [] })
  }
}

export const historyService = new HistoryService()